import React, { useState } from 'react'
import { useLocation } from "react-router-dom"
import { useGenericQuery } from '../../../../../queries/use-generic-query';
import { QUERY_KEYS } from '../../../../../lib/query-constants';
import CommonCard from '../../../../global/card';
import SOURCES from '../../../../../utils/constants';
import EnhancedAdvanceSearchLeadsImported from '../leads-imported/enhancedAdvanceSearchLeadsImported';
import CommonTable from '../../../../global/table';
import { formatDateTime, formatLeadSource } from '../../../../../utils/functions';
import CommonDrawer from '../../../../global/drawer';
import { Button, Row, Col, Tag } from 'antd';
import { InfoCircleOutlined, BugOutlined, UserOutlined, CalendarOutlined } from '@ant-design/icons';
import NoRecordFound from '../../../../global/noRecordFound';
import CommonSkeletonLoader from '../../../../global/loading';
import EnhancedScrappingErrorsGridView from './enhancedScrappingErrorsGridView';

const EnhancedScrappingErrors = () => {
  const [showDetail, setShowDetail] = useState(false);
  const [selectedError, setSelectedError] = useState(null);
  const [sort, setSort] = useState({ sortType: "", sortOrder: 1 });
  const [showAdvanceSearch, setShowAdvanceSearch] = useState(false);
  const [scrappingErrorsViewType, setScrappingErrorViewType] = useState(() => {
    return localStorage.getItem("scrappingErrorsViewType") || "GRID";
  });

  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const pageFromQuery = parseInt(searchParams.get("page") || 1, 10);
  const limitFromQuery = parseInt(
    searchParams.get("limit") || SOURCES.DEFAULT_RECORDS_LISTING,
    10
  );
  const offsetFromQuery = parseInt(searchParams.get("offset") || "0", 10);

  const [pagination, setPagination] = useState({
    offset: offsetFromQuery,
    limit: limitFromQuery,
    page: pageFromQuery,
  });

  const [advanceSearch, setAdvanceSearch] = useState({
    advance_search_enabled: SOURCES.ADVANCE_SEARCH_FIELDS.SEARCH_TYPE_NOT_FILTER,
    startDate: "",
    endDate: "",
    leadSource: "",
    user: "",
  });


  const keyParams = {
    offset: pagination.offset,
    limit: pagination.limit,
    sortBy: sort.sortType,
    sortOrder: sort.sortOrder
  }
  const searchKeyParams = {
    startDate: advanceSearch.startDate,
    endDate: advanceSearch.endDate,
    leadSource: advanceSearch.leadSource,
    userEmail: advanceSearch.user
  }

  // Determine which query to use
  const isSearchMode = advanceSearch.advance_search_enabled === SOURCES.ADVANCE_SEARCH_FIELDS.SEARCH_TYPE_FILTER ||
    advanceSearch.advance_search_enabled === SOURCES.ADVANCE_SEARCH_FIELDS.SEARCH_TYPE_DO_NOT_CALL_API_PARENT;

  const { data: listData, isLoading: listLoading } = useGenericQuery({
    endpoint: "admin/scraperrors",
    queryKey: QUERY_KEYS.scrappingErrors.lists({ ...keyParams }),
    queryParams: { ...keyParams },
    enabled: !isSearchMode
  });

  const { data: searchData, isLoading: searchLoading } = useGenericQuery({
    endpoint: "admin/scraperrors/search",
    queryKey: QUERY_KEYS.scrappingErrors.lists({ ...searchKeyParams }),
    queryParams: { ...searchKeyParams },
    enabled: isSearchMode
  });

  // Unified data source
  const finalData = isSearchMode ? searchData?.logs : listData?.logs;
  const isLoading = isSearchMode ? searchLoading : listLoading;
  const scrapErrorsList = finalData || []; // Assuming functionality returns object with scraperrors array
  const totalRecords = finalData?.totalRecords || 0;

  const columns = [
    {
      title: "User Email",
      dataIndex: "userEmail",
      key: "userEmail",
      sortType: "User Email", // Assuming backend supports sorting by this,
      render: (text) => (
        <span className='text-primary'>{text ? text : "-"}</span>
      )
    },
    {
      title: "Lead Source",
      dataIndex: "leadSource",
      key: "leadSource",
      sortType: "Lead Source"
    },
    {
      title: "Error",
      dataIndex: "error",
      key: "error",
      render: (text) => (
        <span className="truncate max-w-md block" title={text}>
          {text}
        </span>
      )
    },
    {
      title: "DateTime",
      dataIndex: "createdAt",
      key: "createdAt",
      sortType: "createdAt"
    },
  ];

  const tableData = scrapErrorsList.map(item => ({
    key: item._id,
    userEmail: item.userEmail,
    leadSource: formatLeadSource(item.leadSource),
    error: item.errorLog,
    createdAt: formatDateTime(item.createdAt),
    // store original item for details
    original: item
  }));

  const handlePaginationChange = (page, pageSize) => {
    const newOffset = (page - 1) * pageSize;
    setPagination({
      offset: newOffset,
      limit: pageSize,
      page: page,
    });
  };

  return (
    <>
      <CommonCard
        isAdvanceSearch={{
          show: true,
          filter: true,
          onClick: () => setShowAdvanceSearch(!showAdvanceSearch),
          children: <EnhancedAdvanceSearchLeadsImported
            isOpen={showAdvanceSearch}
            advanceSearch={advanceSearch}
            setShowAdvanceSearch={setShowAdvanceSearch}
            setAdvanceSearch={setAdvanceSearch}
            getAdvanceSearchData={() => { }}
            showSearchTerm={false}
          />
        }}
        isListGridView={{
          show: true,
          setViewType: setScrappingErrorViewType,
          viewType: scrappingErrorsViewType,
          viewFor: "scrappingErrorsViewType"
        }}
        isLoading={{
          show: isLoading,
          loaderCmp: <CommonSkeletonLoader
            rows={9}
            type={scrappingErrorsViewType === "GRID" ? "card" : "table"}
            tableLength={8} />
        }}
      >
        {tableData.length > 0 ? (
          <>
            {scrappingErrorsViewType === "LIST" ? (
              <CommonTable
                columns={columns}
                data={tableData}
                sort={sort}
                setSort={setSort}
                pagination={{
                  current: pagination.page,
                  pageSize: pagination.limit,
                  total: totalRecords,
                  onChange: handlePaginationChange,
                }}
                onRowClick={(record) => {
                  setSelectedError(record.original);
                  setShowDetail(true);
                }}
              />
            ) : (
              <EnhancedScrappingErrorsGridView
                data={tableData}
                onRowClick={(record) => {
                  setSelectedError(record.original);
                  setShowDetail(true);
                }}
              />
            )}
          </>
        ) : (
          <NoRecordFound message="No scrapping errors found." />
        )}
      </CommonCard>

      {showDetail && selectedError && (
        <CommonDrawer
          open={showDetail}
          setOpen={setShowDetail}
          title="Scrapping Error Detail"
          width="600px"
        >
          <div className="p-6" style={{ backgroundColor: "#f5f7fa", minHeight: "100%" }}>
            <CommonCard
              title={
                <div className="flex items-center gap-2">
                  <BugOutlined style={{ color: "var(--primary-color, #1890ff)", fontSize: "18px" }} />
                  <span style={{ fontSize: "16px", fontWeight: 600 }}>Error Details</span>
                </div>
              }
            >
              <Row gutter={[24, 0]}>
                {/* Basic Info */}
                <Col span={24} className="border-b border-gray-200 py-4">
                  <div className="flex items-center gap-2 mb-4">
                    <InfoCircleOutlined style={{ color: "var(--primary-color, #1890ff)", fontSize: "16px" }} />
                    <span style={{ fontSize: "15px", fontWeight: 600 }}>Basic Information</span>
                  </div>
                  <Row gutter={[24, 16]}>
                    {[
                      { label: "User Email", value: selectedError?.userEmail, icon: <UserOutlined /> },
                      { label: "Lead Source", value: formatLeadSource(selectedError?.leadSource), isTag: true, icon: <InfoCircleOutlined /> },
                      { label: "Date Time", value: formatDateTime(selectedError?.createdAt), icon: <CalendarOutlined /> },
                    ].map((item) => (
                      <Col span={12} key={item.label}>
                        <div className="flex flex-col">
                          <span className="text-xs text-gray-500 flex items-center gap-2 mb-1">
                            <span style={{ color: "var(--primary-color, #1890ff)" }}>
                              {item.icon}
                            </span>
                            {item.label}
                          </span>
                          {item.isTag ? (
                            <Tag color="blue" className="m-0 px-3 py-1 font-medium capitalize w-fit">
                              {item.value || "—"}
                            </Tag>
                          ) : (
                            <span className="text-sm font-semibold text-gray-900">{item.value || "—"}</span>
                          )}
                        </div>
                      </Col>
                    ))}
                  </Row>
                </Col>

                {/* Error Log */}
                <Col span={24} className="py-4">
                  <div className="flex items-center gap-2 mb-4">
                    <BugOutlined style={{ color: "var(--primary-color, #1890ff)", fontSize: "16px" }} />
                    <span style={{ fontSize: "15px", fontWeight: 600 }}>Error Log</span>
                  </div>
                  <div className="bg-gray-50 p-3 rounded border overflow-auto max-h-[500px]">
                    <code className="text-sm whitespace-pre-wrap break-words">
                      {selectedError?.errorLog}
                    </code>
                  </div>
                </Col>
              </Row>
            </CommonCard>
          </div>
        </CommonDrawer>
      )}
    </>
  )
}

export default EnhancedScrappingErrors